import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAccount } from '@littlebooker/dal';
import { useAuth } from '@/contexts/AuthContext';

type ImageRatio = 'landscape' | 'portrait';

/**
 * Hook to get the preferred image ratio from account settings.
 * Pass an accountId for invite pages (no auth context); omit to use the current session account.
 * Falls back to 'landscape' if not set.
 */
export function usePreferredImageRatio(accountId?: string | null): ImageRatio {
  const { account } = useAuth();
  const id = accountId || account?.id;

  const { data: accountData } = useQuery({
    queryKey: ['account', id],
    queryFn: () => (id ? getAccount(id) : null),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    if (accountData?.settings && typeof accountData.settings === 'object') {
      const settings = accountData.settings as Record<string, unknown>;
      if (settings.preferred_image_ratio === 'portrait') {
        return 'portrait';
      }
    }
    return 'landscape';
  }, [accountData?.settings]);
}
